import { createSlice } from '@reduxjs/toolkit';

/**
 * Student Management Slice
 *
 * Manages state for admin student management features:
 * - Student access requests (pending enrollment requests)
 * - Student list management
 * - Statistics and counts
 */

const initialState = {
  // Access Requests
  accessRequests: [],
  accessRequestsPagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  },
  accessRequestsLoading: false,
  accessRequestStats: {
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0
  },

  // Students list (for future use)
  students: [],
  studentsPagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  },
  studentsLoading: false,

  // Class-wise dashboard
  classWiseOverview: {
    classes: [],
    summary: {
      totalStudents: 0,
      totalPending: 0,
      totalNotJoined: 0,
      academicYear: ''
    }
  },
  classWiseOverviewLoading: false,
  classDetail: null,
  classDetailLoading: false,
  selectedClassId: null,

  // General
  error: null
};

const studentManagementSlice = createSlice({
  name: 'studentManagement',
  initialState,
  reducers: {
    // Access Requests
    setAccessRequestsLoading: (state, action) => {
      state.accessRequestsLoading = action.payload;
    },

    setAccessRequests: (state, action) => {
      state.accessRequests = action.payload.requests || [];
      state.accessRequestsPagination = action.payload.pagination || state.accessRequestsPagination;
    },

    setAccessRequestStats: (state, action) => {
      state.accessRequestStats = action.payload;
    },

    updateAccessRequest: (state, action) => {
      const { id, updates } = action.payload;
      const index = state.accessRequests.findIndex(req => req._id === id);
      if (index !== -1) {
        state.accessRequests[index] = { ...state.accessRequests[index], ...updates };
      }
    },

    removeAccessRequest: (state, action) => {
      const id = action.payload;
      state.accessRequests = state.accessRequests.filter(req => req._id !== id);
    },

    // Students
    setStudentsLoading: (state, action) => {
      state.studentsLoading = action.payload;
    },

    setStudents: (state, action) => {
      state.students = action.payload.students || [];
      state.studentsPagination = action.payload.pagination || state.studentsPagination;
    },

    // General
    setError: (state, action) => {
      state.error = action.payload;
    },

    clearError: (state) => {
      state.error = null;
    },

    // Class-wise Dashboard
    setClassWiseOverviewLoading: (state, action) => {
      state.classWiseOverviewLoading = action.payload;
    },

    setClassWiseOverview: (state, action) => {
      state.classWiseOverview = action.payload;
      state.classWiseOverviewLoading = false;
    },

    setClassDetailLoading: (state, action) => {
      state.classDetailLoading = action.payload;
    },

    setClassDetail: (state, action) => {
      state.classDetail = action.payload;
      state.classDetailLoading = false;
    },

    setSelectedClassId: (state, action) => {
      state.selectedClassId = action.payload;
    },

    clearClassDetail: (state) => {
      state.classDetail = null;
      state.selectedClassId = null;
    },

    // Reset state (e.g., on tenant change)
    resetState: () => initialState
  }
});

export const {
  setAccessRequestsLoading,
  setAccessRequests,
  setAccessRequestStats,
  updateAccessRequest,
  removeAccessRequest,
  setStudentsLoading,
  setStudents,
  setError,
  clearError,
  resetState,
  setClassWiseOverviewLoading,
  setClassWiseOverview,
  setClassDetailLoading,
  setClassDetail,
  setSelectedClassId,
  clearClassDetail
} = studentManagementSlice.actions;

// Selectors
export const selectAccessRequests = (state) => state.studentManagement.accessRequests;
export const selectAccessRequestsPagination = (state) => state.studentManagement.accessRequestsPagination;
export const selectAccessRequestsLoading = (state) => state.studentManagement.accessRequestsLoading;
export const selectAccessRequestStats = (state) => state.studentManagement.accessRequestStats;
export const selectStudents = (state) => state.studentManagement.students;
export const selectStudentsLoading = (state) => state.studentManagement.studentsLoading;

// Class-wise dashboard selectors
export const selectClassWiseOverview = (state) => state.studentManagement.classWiseOverview;
export const selectClassWiseOverviewLoading = (state) => state.studentManagement.classWiseOverviewLoading;
export const selectClassDetail = (state) => state.studentManagement.classDetail;
export const selectClassDetailLoading = (state) => state.studentManagement.classDetailLoading;
export const selectSelectedClassId = (state) => state.studentManagement.selectedClassId;

export default studentManagementSlice.reducer;
